import React, {useState, useEffect} from "react";
import { isSuperAdmin } from "../utils/roleUtils";
import { apiRequest } from "../api";

const UserModal = ({user, universityId, onClose, onSave, universities, isMultiUser}) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [userRole, setUserRole] = useState("user");
    const [webAccess, setWebAccess] = useState(true);
    const [selectedUniversity, setSelectedUniversity] = useState(universityId || "");
    const [allUniversities, setAllUniversities] = useState([]);
    const [isLoadingUniversities, setIsLoadingUniversities] = useState(false); // Loading state for universities

    const [isSaving, setIsSaving] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    const fetchUniversities = async () => {
        if (isSuperAdmin()) {
            setIsLoadingUniversities(true);
            try {
                const response = await fetch(`${apiUrl}/universities`);
                if (!response.ok) throw new Error("Failed to fetch universities.");
                const data = await response.json();
                setAllUniversities(data);
            } catch (error) {
                console.error("Error fetching universities:", error);
                setAllUniversities([]);
            } finally {
                setIsLoadingUniversities(false);
            }
        }
    };

    useEffect(() => {
        fetchUniversities();
    }, []); // Fetch universities on component mount

    useEffect(() => {
        if (user) {
            setFirstName(user.first_name);
            setLastName(user.last_name);
            setEmail(user.email);
            setUserRole(user.role);
            setWebAccess(user.web_access !== false);
            setSelectedUniversity(user.university?.id || "");
        } else {
            setFirstName("");
            setLastName("");
            setEmail("");
            setUserRole("user");
            setWebAccess(true);
            setSelectedUniversity(universityId || "");
        }
    }, [user, universityId]);

    const effectiveRole = (isSuperAdmin() || isMultiUser) ? userRole : "user";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setFeedbackMessage("");

        // For multi-users, require university selection
        if (isMultiUser && !selectedUniversity) {
            setFeedbackMessage("Please select a university.");
            setIsSaving(false);
            return;
        }

        const payload = {
            first_name: firstName,
            last_name: lastName,
            email,
            role: effectiveRole,
            university_id: (isSuperAdmin() || isMultiUser) ? selectedUniversity : universityId,
            // Admin roles are web-only by nature, so the toggle applies to standard users.
            web_access: effectiveRole === "user" ? webAccess : true,
        };

        const endpoint = user
            ? `${apiUrl}/users/${user.id}`
            : `${apiUrl}/users`;
        const method = user ? "PUT" : "POST";
        const token = localStorage.getItem("token");

        try {
            const response = await apiRequest(endpoint, method, payload, token);
            setFeedbackMessage("User saved successfully.");
            
            // If updating current user's profile, refresh their role information
            const currentUserId = localStorage.getItem("userId");
            if (user && currentUserId && user.id === currentUserId) {
                // Update localStorage with new role information
                localStorage.setItem("role", response.user.role);
                if (response.user.role_details) {
                    localStorage.setItem("roleDetails", JSON.stringify(response.user.role_details));
                }
                // Force page reload to update sidebar
                window.location.reload();
            }
            
            onSave();
            setTimeout(onClose, 2000); // Close modal after 2 seconds
        } catch (error) {
            console.error(error);
            setFeedbackMessage(error.message || "An error occurred while saving.");
        } finally {
            setIsSaving(false);
        }
    };

    // Use provided universities for multi-users, or fetch all for super admins
    const availableUniversities = isMultiUser ? universities : allUniversities;

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "5px",
                    width: "400px",
                }}
            >
                <h4>{user ? "Edit User" : "Add User"}</h4>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>First Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Last Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            className="form-control"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    {(isSuperAdmin() || isMultiUser) && (
                        <div className="form-group">
                            <label>Role</label>
                            <select
                                className="form-control"
                                value={userRole}
                                onChange={(e) => setUserRole(e.target.value)}
                            >
                                <option value="user">User</option>
                                <option value="uni_admin">University Admin</option>
                                {isSuperAdmin() && <option value="multi_user">Multi User</option>}
                                {isSuperAdmin() && <option value="super_admin">Super Admin</option>}
                            </select>
                        </div>
                    )}
                    {(isSuperAdmin() || isMultiUser) && (
                        <div className="form-group">
                            <label>University</label>
                            <select
                                className="form-control"
                                value={selectedUniversity}
                                onChange={(e) => setSelectedUniversity(e.target.value)}
                                required={isMultiUser}
                            >
                                <option value="" disabled>
                                    Select a University
                                </option>
                                {availableUniversities.map((uni) => (
                                    <option key={uni.id} value={uni.id}>
                                        {uni.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {effectiveRole === "user" && (
                        <div className="form-group">
                            <label>Access</label>
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="webAccessCheck"
                                    checked={webAccess}
                                    onChange={(e) => setWebAccess(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="webAccessCheck">
                                    Allow web app access
                                </label>
                            </div>
                            <small className="form-text text-muted">
                                Uncheck to make this a mobile-only account. The user will still be
                                able to sign in to the mobile app, but not the web app.
                            </small>
                        </div>
                    )}
                    <div className="form-footer">
                        {isSaving ? (
                            <p>Saving...</p>
                        ) : (
                            <button type="submit" className="btn btn-primary">
                                Save
                            </button>
                        )}
                        &nbsp;
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
                {feedbackMessage && (
                    <p
                        style={{
                            marginTop: "10px",
                            color: feedbackMessage.includes("successfully")
                                ? "green"
                                : "red",
                        }}
                    >
                        {feedbackMessage}
                    </p>
                )}
            </div>
        </div>
    );
};

export default UserModal;